import React, {useEffect, useMemo, useState} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {
    Box, Card, Chip, CircularProgress, Divider, Tooltip, Typography, Button
} from "@mui/material";
import {Undo, CheckCircle as CheckCircleIcon} from "@mui/icons-material";
import moment from "moment";

import API from "../../../../utils/API";
import {API_ENDPOINTS} from "../../../../apiConfig";
import Forms from "../../../../components/Forms";
import Error from "../../../../components/Error";

const currencyFmt = (amount, currency) =>
    new Intl.NumberFormat("cs-CZ", {style: "currency", currency}).format(Number(amount) || 0);

// Součet totalů z totalPrice.gross (už je za celou položku)
function computeTotals(order) {
    const byCurrency = new Map();
    for (const it of order?.partList || []) {
        const curr = it.totalPrice?.currency || "CZK";
        const gross = Number(it.totalPrice?.gross) || 0;
        byCurrency.set(curr, (byCurrency.get(curr) || 0) + gross);
    }
    // aplikuj slevu na GROSS
    const disc = order?.discount;
    if (disc?.type && (disc.value || disc.value === 0)) {
        if (disc.type === "percentage") {
            const pct = Number(disc.value) || 0;
            for (const [c, amt] of byCurrency) byCurrency.set(c, amt * (1 - pct / 100));
        } else if (disc.type === "fixed" && byCurrency.size === 1) {
            const [[c, amt]] = Array.from(byCurrency.entries());
            byCurrency.set(c, Math.max(0, amt - (Number(disc.value) || 0)));
        }
    }
    return byCurrency;
}

const EditPartOrder = () => {
    const {id} = useParams();
    const {t} = useTranslation();
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState();
    const [order, setOrder] = useState(null);

    // ===== LOAD =====
    const load = async () => {
        setIsLoading(true);
        setError(undefined);
        try {
            const {data} = await API.get(API_ENDPOINTS.partOrders.get(id));
            setOrder(data);
        } catch (e) {
            setError(e);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [id]);

    // Totals Map<currency, amount>
    const totals = useMemo(() => computeTotals(order), [order]);

    const markDelivered = async (partListId) => {
        try {
            const {data} = await API.put(API_ENDPOINTS.partOrders.markDelivered(id), {partListId});
            setOrder(data);
        } catch (e) {
            setError(e);
        }
    };

    if (isLoading) {
        return (
            <Box className="flex items-center justify-center h-80">
                <CircularProgress/>
            </Box>
        );
    }
    if (error) {
        return (
            <Box className="p-6">
                <Error error={error}/>
                <Forms.Button className="mt-4" label={t("reload")} onClick={load}/>
            </Box>
        );
    }
    if (!order) return null;

    const finished = !!order.finishedDate;
    const showMarkDelivered = (it) => !it.delivered;

    return (
        <Box className="p-6 space-y-6">
            {/* Header */}
            <Box className="flex items-start justify-between">
                <div>
                    <Typography variant="h5" className="mb-1">
                        {t("partOrders.detail")}
                    </Typography>
                    <div className="text-sm text-gray-600">
                        #{order._id} • {t("createdAt")}: {moment(order.createdAt).format("DD.MM.YYYY HH:mm")}
                    </div>
                    <div className="text-sm text-gray-600">
                        {t("label")}: <b>{order.label}</b>
                    </div>
                </div>

                <Box className="flex items-end gap-4">
                    <div className="text-lg text-right">
                        {t("status")}:{" "}
                        <b>
                            {finished ? t("partOrders.finished") : t("partOrders.inProgress")}
                        </b>
                        {order.finishedDate && (
                            <p>{t("partOrders.finishedAt")}: {moment(order.finishedDate).format("DD.MM.YYYY HH:mm")}</p>
                        )}
                    </div>
                    <Forms.Button
                        variant="text"
                        leftIcon={<Undo/>}
                        label={t("back")}
                        onClick={() => navigate(-1)}
                    />
                </Box>
            </Box>

            {/* Základní info */}
            <Card className="p-4">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <div className="text-sm text-gray-600">{t("label")}</div>
                        <div className="font-medium">{order.label}</div>
                    </div>
                    <div>
                        <div className="text-sm text-gray-600">{t("description")}</div>
                        <div>{order.description || "—"}</div>
                    </div>
                    <div>
                        <div className="text-sm text-gray-600">{t("orderDate")}</div>
                        <div>{order.orderDate ? moment(order.orderDate).format("DD.MM.YYYY") : "—"}</div>
                    </div>
                    <div>
                        <div className="text-sm text-gray-600">{t("createdBy")}</div>
                        <div>{order.createdBy ? `${order.createdBy.name} ${order.createdBy.surname}` : "—"}</div>
                    </div>
                </div>

                <Divider className="my-4"/>

                <div className="grid grid-cols-3 gap-4">
                    <div>
                        <div className="text-sm text-gray-600 mb-1">{t("total")}</div>
                        <div className="text-base">
                            {Array.from(totals.entries()).map(([curr, amt]) => (
                                <span key={curr} className="mr-3">
                  {currencyFmt(amt, curr)}
                </span>
                            ))}
                        </div>
                    </div>
                    <div>
                        <div className="text-sm text-gray-600 mb-1">{t("discount")}</div>
                        <div>
                            {order.discount?.type
                                ? `${t(`discountType.${order.discount.type}`)} · ${order.discount.value}`
                                : "—"}
                        </div>
                    </div>
                </div>
            </Card>

            {/* Položky */}
            <Box className="space-y-4">
                {(order.partList || []).map((it) => {
                    const partName =
                        typeof it.part === "object" ? it.part.name : String(it.part);
                    const models =
                        typeof it.part === "object"
                            ? (it.part?.part?.compatibleWith || []).map(c => c.code || c.name).join(", ")
                            : "";

                    const unit = it.pricePerUnit || {};
                    const total = it.totalPrice || {};
                    const delivered = !!it.delivered;

                    return (
                        <Card key={it._id} className="p-4">
                            <div className="flex items-center justify-between gap-3">
                                <div className="flex items-center gap-3">
                                    <Chip label={<span className="font-medium">{partName}</span>} variant="outlined"/>
                                    {models && (
                                        <span className="text-sm text-gray-600">{models}</span>
                                    )}
                                </div>

                                <div className="flex items-center gap-3">
                                    <Chip
                                        color={delivered ? "success" : "default"}
                                        icon={<CheckCircleIcon/>}
                                        label={delivered ? t("partOrders.itemDelivered") : t("partOrders.itemPending")}
                                        variant={delivered ? "filled" : "outlined"}
                                    />
                                    {
                                        showMarkDelivered(it) &&
                                        <Tooltip
                                            title={delivered ? t("partOrders.alreadyDelivered") : t("partOrders.markDelivered")}>
                                            <span>
                                                <Button
                                                    variant="outlined"
                                                    color="success"
                                                    startIcon={<CheckCircleIcon/>}
                                                    onClick={() => markDelivered(it._id)}
                                                    disabled={delivered}
                                                >
                                                    {t("partOrders.markDelivered")}
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    }
                                </div>
                            </div>

                            <Divider className="my-3"/>

                            <div className="grid grid-cols-12 gap-3 text-sm">
                                <div className="col-span-3">
                                    <div className="text-gray-600">{t("orderId")}</div>
                                    <div className="font-medium">{it.orderId || "—"}</div>
                                </div>
                                <div className="col-span-2">
                                    <div className="text-gray-600">{t("quantity")}</div>
                                    <div className="font-medium">{it.quantity}</div>
                                </div>
                                <div className="col-span-7">
                                    <div className="text-gray-600">{t("pricePerUnit")}</div>
                                    <div className="font-medium">
                                        {t("net")}: {currencyFmt(unit.net, unit.currency || "CZK")}
                                        {" · "}
                                        {t("gross")}: {currencyFmt(unit.gross, unit.currency || "CZK")}
                                        {"  "}
                                        ({t("vatRate")}: {Number(unit.vatRate) || 0}%)
                                    </div>
                                </div>

                                <div className="col-span-12">
                                    <div className="text-gray-600">{t("totalPrice")}</div>
                                    <div className="font-medium">
                                        {t("net")}: {currencyFmt(total.net, total.currency || "CZK")}
                                        {" · "}
                                        {t("gross")}: {currencyFmt(total.gross, total.currency || "CZK")}
                                        {"  "}
                                        ({t("vatRate")}: {Number(total.vatRate) || 0}%)
                                    </div>
                                </div>
                            </div>
                        </Card>
                    );
                })}
            </Box>

            <Box className="flex justify-end">
                <Forms.Button
                    variant="text"
                    leftIcon={<Undo/>}
                    label={t("back")}
                    onClick={() => navigate(-1)}
                />
            </Box>
        </Box>
    );
};

export default EditPartOrder;
